import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot as BotIcon, History, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { botsApi } from '@/api/bots';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { Bot, BotCreateInput, BotPatchInput, BotPromptSections } from '@/api/types';
import { normalizePromptSections } from '@/lib/prompt-sections';
import { BotPromptEditor } from './BotPromptEditor';
import { PromptHistoryDialog } from './PromptHistoryDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const DEFAULT_MODEL_SENTINEL = '__default__';

function initialPromptSections(bot: Bot | undefined): BotPromptSections {
  if (bot?.promptSections) return bot.promptSections;
  if (bot?.systemPrompt) return { freeform: bot.systemPrompt };
  return {};
}

export function BotsTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentOrgId, currentOrg, bots, currentBotId, setBot, refreshBots } =
    useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Bot | null>(null);
  const [historyBotId, setHistoryBotId] = React.useState<string | null>(null);
  const historyBot = bots.find((b) => b.botId === historyBotId) ?? null;

  const invalidate = (): void => {
    refreshBots();
    void queryClient.invalidateQueries({ queryKey: ['org', 'bots', 'list', currentOrgId] });
  };

  const deleteMutation = useMutation({
    mutationFn: (botId: string) => botsApi.delete(botId),
    onSuccess: (_data, botId) => {
      toast.success(t('organization.bots.deletedToast'));
      if (currentBotId === botId) setBot(null);
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const togglEnabledMutation = useMutation({
    mutationFn: ({ botId, enabled }: { botId: string; enabled: boolean }) =>
      botsApi.patch(botId, { enabled }),
    onSuccess: () => {
      toast.success(t('organization.bots.updatedToast'));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">{t('organization.bots.title')}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(bots.length > 1 ? 'organization.bots.countMany' : 'organization.bots.countOne', { count: bots.length })}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          disabled={!canManage}
          className="gap-1.5"
          title={canManage ? t('organization.bots.create') : t('organization.bots.newRestricted')}
        >
          <Plus className="h-4 w-4" />
          {t('organization.bots.new')}
        </Button>
      </CardHeader>
      <CardContent>
        {bots.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('organization.bots.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('organization.bots.headers.name')}</TableHead>
                <TableHead>{t('organization.bots.headers.id')}</TableHead>
                <TableHead>{t('organization.bots.headers.status')}</TableHead>
                <TableHead>{t('organization.bots.headers.version')}</TableHead>
                <TableHead>{t('organization.bots.headers.updated')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {bots.map((b) => {
                const isCurrent = b.botId === currentBotId;
                return (
                  <TableRow key={b.botId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <BotIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{b.name}</span>
                        {isCurrent ? (
                          <Badge variant="secondary" className="text-[10px]">
                            {t('organization.bots.selected')}
                          </Badge>
                        ) : null}
                      </div>
                      {b.description ? (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {b.description}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{b.botId}</TableCell>
                    <TableCell>
                      {b.enabled ? (
                        <Badge variant="success">{t('organization.bots.active')}</Badge>
                      ) : (
                        <Badge variant="warning">{t('organization.bots.disabled')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1.5 px-2 font-mono text-xs"
                        title={t('organization.bots.history')}
                        onClick={() => setHistoryBotId(b.botId)}
                      >
                        <History className="h-3.5 w-3.5 text-muted-foreground" />
                        {b.promptVersion ? `v${b.promptVersion}` : '—'}
                      </Button>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(b.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={isCurrent}
                            onClick={() => setBot(b.botId)}
                          >
                            {t('organization.bots.selectInWorkspace')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={!canManage}
                            onClick={() => setEditing(b)}
                          >
                            {t('organization.bots.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setHistoryBotId(b.botId)}>
                            {t('organization.bots.history')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!canManage}
                            onClick={() =>
                              togglEnabledMutation.mutate({
                                botId: b.botId,
                                enabled: !b.enabled,
                              })
                            }
                          >
                            {b.enabled ? t('organization.bots.deactivate') : t('organization.bots.activate')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={!canManage}
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              if (window.confirm(t('organization.bots.confirmDelete', { name: b.name }))) {
                                deleteMutation.mutate(b.botId);
                              }
                            }}
                          >
                            {t('organization.bots.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <BotFormDialog
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={invalidate}
      />
      {editing ? (
        <BotFormDialog
          mode="edit"
          bot={editing}
          open={!!editing}
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
          onSaved={invalidate}
        />
      ) : null}
      {historyBot ? (
        <PromptHistoryDialog
          bot={historyBot}
          open={!!historyBot}
          onOpenChange={(o) => {
            if (!o) setHistoryBotId(null);
          }}
          canManage={canManage}
        />
      ) : null}
    </Card>
  );
}

function BotFormDialog({
  mode,
  bot,
  open,
  onOpenChange,
  onSaved,
}: {
  mode: 'create' | 'edit';
  bot?: Bot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = React.useState(bot?.name ?? '');
  const [description, setDescription] = React.useState(bot?.description ?? '');
  const [promptSections, setPromptSections] = React.useState<BotPromptSections>(() =>
    initialPromptSections(bot),
  );
  const [llmModel, setLlmModel] = React.useState(bot?.llmModel ?? '');
  const [enabled, setEnabled] = React.useState(bot?.enabled ?? true);
  const [versionNote, setVersionNote] = React.useState('');

  const models = useQuery({
    queryKey: ['org-llm-models'],
    queryFn: () => botsApi.models(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  React.useEffect(() => {
    if (open && mode === 'edit' && bot) {
      setName(bot.name);
      setDescription(bot.description ?? '');
      setPromptSections(initialPromptSections(bot));
      setLlmModel(bot.llmModel ?? '');
      setEnabled(bot.enabled);
      setVersionNote('');
    }
    if (open && mode === 'create') {
      setName('');
      setDescription('');
      setPromptSections({});
      setLlmModel('');
      setEnabled(true);
    }
  }, [open, mode, bot]);

  const createMutation = useMutation({
    mutationFn: (input: BotCreateInput) => botsApi.create(input),
    onSuccess: () => {
      toast.success(t('organization.bots.createdToast'));
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const patchMutation = useMutation({
    mutationFn: ({ botId, input }: { botId: string; input: BotPatchInput }) =>
      botsApi.patch(botId, input),
    onSuccess: (saved) => {
      toast.success(
        saved.promptVersion && saved.promptVersion !== bot?.promptVersion
          ? t('organization.bots.versionSavedToast', { version: saved.promptVersion })
          : t('organization.bots.updatedToast'),
      );
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!name.trim()) return;
    const sections = normalizePromptSections(promptSections);
    const hasSections = Object.keys(sections).length > 0;
    if (mode === 'create') {
      createMutation.mutate({
        name: name.trim(),
        enabled,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(hasSections ? { promptSections: sections } : {}),
        ...(llmModel ? { llmModel } : {}),
      });
    } else if (bot) {
      const patch: BotPatchInput = {};
      if (name.trim() !== bot.name) patch.name = name.trim();
      if (enabled !== bot.enabled) patch.enabled = enabled;
      const diffNullable = (
        next: string,
        prev: string | undefined,
        key: keyof BotPatchInput,
      ): void => {
        const trimmed = next.trim();
        if (trimmed === (prev ?? '')) return;
        (patch as Record<string, unknown>)[key] = trimmed ? trimmed : null;
      };
      diffNullable(description, bot.description, 'description');
      diffNullable(llmModel, bot.llmModel, 'llmModel');
      const initialSections = normalizePromptSections(initialPromptSections(bot));
      if (JSON.stringify(sections) !== JSON.stringify(initialSections)) {
        patch.promptSections = hasSections ? sections : null;
      }
      if ((patch.promptSections !== undefined || patch.llmModel !== undefined) && versionNote.trim()) {
        patch.versionNote = versionNote.trim();
      }
      if (Object.keys(patch).length === 0) {
        onOpenChange(false);
        return;
      }
      patchMutation.mutate({ botId: bot.botId, input: patch });
    }
  };

  const busy = createMutation.isPending || patchMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' ? t('organization.bots.dialog.createTitle') : t('organization.bots.dialog.editTitle')}
            </DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? t('organization.bots.dialog.createSubtitle')
                : t('organization.bots.dialog.editSubtitle', { id: bot?.botId })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="bot-name">{t('organization.bots.dialog.name')}</Label>
              <Input
                id="bot-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('organization.bots.dialog.namePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="bot-desc">{t('organization.bots.dialog.description')}</Label>
              <Textarea
                id="bot-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('organization.bots.dialog.descriptionPlaceholder')}
              />
            </div>
            <div>
              <BotPromptEditor
                key={open ? 'open' : 'closed'}
                value={promptSections}
                onChange={setPromptSections}
              />
              <DefaultPromptDisclosure />
            </div>
            <div>
              <Label htmlFor="bot-llm-model">{t('organization.bots.dialog.llmModel')}</Label>
              <Select
                value={llmModel || DEFAULT_MODEL_SENTINEL}
                onValueChange={(v) => setLlmModel(v === DEFAULT_MODEL_SENTINEL ? '' : v)}
              >
                <SelectTrigger id="bot-llm-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT_MODEL_SENTINEL}>
                    {t('organization.bots.dialog.llmModelDefault', {
                      model: models.data?.default.model ?? '…',
                    })}
                  </SelectItem>
                  {llmModel && !(models.data?.models ?? []).some((m) => m.id === llmModel) ? (
                    <SelectItem value={llmModel}>{llmModel}</SelectItem>
                  ) : null}
                  {models.isPending ? (
                    <div className="px-8 py-1.5 text-sm text-muted-foreground">
                      {t('organization.bots.dialog.llmModelLoading')}
                    </div>
                  ) : null}
                  {models.isError ? (
                    <div className="px-8 py-1.5 text-sm text-destructive">
                      {t('organization.bots.dialog.llmModelError')}
                    </div>
                  ) : null}
                  {(models.data?.models ?? []).some((m) => m.backend === 'openai') ? (
                    <SelectGroup>
                      <SelectLabel>OpenAI</SelectLabel>
                      {(models.data?.models ?? [])
                        .filter((m) => m.backend === 'openai')
                        .map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.id}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  ) : null}
                  {(models.data?.models ?? []).some((m) => m.backend === 'google-ai') ? (
                    <SelectGroup>
                      <SelectLabel>Google AI</SelectLabel>
                      {(models.data?.models ?? [])
                        .filter((m) => m.backend === 'google-ai')
                        .map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.id}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  ) : null}
                  {(models.data?.models ?? []).some((m) => m.backend === 'anthropic') ? (
                    <SelectGroup>
                      <SelectLabel>Anthropic</SelectLabel>
                      {(models.data?.models ?? [])
                        .filter((m) => m.backend === 'anthropic')
                        .map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.id}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  ) : null}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('organization.bots.dialog.llmModelHelp')}
              </p>
            </div>
            {mode === 'edit' ? (
              <div>
                <Label htmlFor="bot-version-note">{t('organization.bots.dialog.versionNote')}</Label>
                <Input
                  id="bot-version-note"
                  value={versionNote}
                  maxLength={280}
                  onChange={(e) => setVersionNote(e.target.value)}
                  placeholder={t('organization.bots.dialog.versionNotePlaceholder')}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('organization.bots.dialog.versionNoteHelp', {
                    version: bot?.promptVersion ?? '—',
                  })}
                </p>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Checkbox
                id="bot-enabled"
                checked={enabled}
                onCheckedChange={(c) => setEnabled(c === true)}
              />
              <Label htmlFor="bot-enabled" className="cursor-pointer">
                {t('organization.bots.dialog.enabled')}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mode === 'create' ? t('common.create') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DefaultPromptDisclosure(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const prompts = useQuery({
    queryKey: ['bot-default-prompts'],
    queryFn: () => botsApi.defaultPrompts(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        {open
          ? t('organization.bots.dialog.defaultPrompt.hide')
          : t('organization.bots.dialog.defaultPrompt.show')}
      </button>
      {open ? (
        <div className="mt-2 space-y-3 rounded-md border bg-muted/40 p-3">
          <p className="text-xs text-muted-foreground">
            {t('organization.bots.dialog.defaultPrompt.intro')}
          </p>
          {prompts.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('common.loading')}
            </div>
          ) : null}
          {prompts.error ? (
            <p className="text-xs text-destructive">{unwrapError(prompts.error)}</p>
          ) : null}
          {prompts.data ? (
            <>
              <DefaultPromptBlock
                label={t('organization.bots.dialog.defaultPrompt.ops')}
                text={prompts.data.ops}
              />
              <DefaultPromptBlock
                label={t('organization.bots.dialog.defaultPrompt.support')}
                text={prompts.data.support}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DefaultPromptBlock({ label, text }: { label: string; text: string }): JSX.Element {
  return (
    <div>
      <p className="mb-1 text-xs font-medium">{label}</p>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
        {text}
      </pre>
    </div>
  );
}
